import React, { useEffect } from 'react';
import classnames from 'classnames';
import { camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items } from 'src/queries/__generated__/wearerBundleDetail';
import { FormikValuesBundlesType } from 'src/MixAndMatchBundle/MixAndMatchBundle';
import { CircleLoader } from 'src/components/CircleLoader/CircleLoader';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { useWearerBundles } from 'src/hooks/useWearerBundles';
import { BundleTypeValues, BaseBundleType } from 'src/components/BundleProduct/types/index';

import classes from './MixAndMatchBundleFooter.scss';

type Props = {
    bundleTitle: string;
    categories: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items[];
    bundles: FormikValuesBundlesType[];
    isFormValid: boolean;
    onClick: () => void;
    basketLoading: boolean;
    setWearerBundleData: (data: BundleTypeValues[] | BaseBundleType[]) => void;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    isDataLoaded: boolean;
    loading: boolean;
};

const SubmitButton: React.FC<{
    waitForData: boolean;
    isFormValid: boolean;
    isMoreThan3Bundles: boolean;
    basketLoading: boolean;
    onClick: () => void;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    isDataLoaded: boolean;
    setWearerBundleData: (data: BundleTypeValues[] | BaseBundleType[]) => void;
}> = ({
    waitForData,
    isFormValid,
    isMoreThan3Bundles,
    basketLoading,
    onClick,
    wearerOptions,
    summary,
    isDataLoaded,
    setWearerBundleData,
}) => {
    const { array: wearerBundleData, loading } = useWearerBundles(wearerOptions, summary);
    useEffect(() => {
        if (!loading && wearerBundleData && !isDataLoaded) {
            setWearerBundleData(wearerBundleData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, wearerBundleData, isDataLoaded]);

    return (
        <button
            type="button"
            disabled={!isFormValid}
            className={classnames(classes.submitButton, {
                [classes.submitButtonBlocked]: !isFormValid,
                [classes.submitButtonExtended]: isMoreThan3Bundles,
            })}
            onClick={() => onClick()}
        >
            {!isFormValid ? (
                'Complete the bundle to continue'
            ) : basketLoading || loading || waitForData ? (
                <div className={classes.loaderWrapper}>
                    <CircleLoader />
                </div>
            ) : (
                <span className={classes.buttonContentValid}>
                    <span className={classes.buttonContentValidText}>Add to Basket</span>
                </span>
            )}
        </button>
    );
};

export const MixAndMatchBundleFooter: React.FC<Props> = ({
    bundleTitle,
    categories,
    bundles,
    isFormValid,
    onClick,
    basketLoading,
    setWearerBundleData,
    wearerOptions,
    summary,
    isDataLoaded,
    loading,
}) => {
    const isMoreThan3Bundles = categories.length > 3;

    const calculateQty = (categoryId: number) => {
        const bundleProducts = bundles.find((bundle) => bundle.id === categoryId)?.products;

        return bundleProducts
            ? bundleProducts?.reduce(
                  (sum, { selectedColorsAndSizes }) =>
                      sum + selectedColorsAndSizes.reduce((sum, { sizes }) => sum + (sizes?.quantity || 0), 0),
                  0,
              )
            : 0;
    };

    const SubmitButtonDummy = () => (
        <div
            className={classnames(classes.submitButton, classes.submitButtonBlocked, {
                [classes.submitButtonExtended]: isMoreThan3Bundles,
            })}
        >
            Complete the bundle to continue
        </div>
    );

    return (
        <div className={classes.footer}>
            <div className={classes.container}>
                <div
                    className={classnames(classes.titleAndQtyContainer, {
                        [classes.titleAndQtyBigContainer]: isMoreThan3Bundles,
                    })}
                >
                    <span className={classes.bundleTitleDesktop}>{bundleTitle}</span>
                    <span className={classes.bundleTitleMobile}>Bundle</span>

                    <div
                        className={classnames(classes.bundleElementsQtyContainer, {
                            [classes.bundleElementsBigQtyContainer]: isMoreThan3Bundles,
                        })}
                    >
                        {categories.map((category) => {
                            const currentQty = calculateQty(category.optionId);
                            const isQtyCorrect = currentQty === category.maxQty;
                            const isQtyIncorrect = currentQty > category.maxQty;

                            return (
                                <div
                                    className={classnames(classes.categoryContainer, {
                                        [classes.categoryBigContainer]: isMoreThan3Bundles,
                                    })}
                                    key={category.optionId}
                                >
                                    <span
                                        className={classnames(classes.categoryTitle, {
                                            [classes.categoryTitleCorrectQty]: isQtyCorrect,
                                        })}
                                    >
                                        {category.title}
                                    </span>
                                    <div
                                        className={classnames(classes.categoryQtyContainer, {
                                            [classes.incorrectQtyContainer]: isQtyIncorrect,
                                            [classes.correctQtyContainer]: isQtyCorrect,
                                        })}
                                    >
                                        <span>{currentQty}</span>
                                        <span className={classes.requiredQty}>/{category.maxQty}</span>
                                        {isQtyCorrect && (
                                            <img
                                                className={classes.checkMark}
                                                src={require('../../static/icons/GreenCheckMark.png')}
                                                alt=""
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {isFormValid ? (
                    <SubmitButton
                        waitForData={loading}
                        isFormValid={isFormValid}
                        isMoreThan3Bundles={isMoreThan3Bundles}
                        basketLoading={basketLoading}
                        onClick={onClick}
                        wearerOptions={wearerOptions}
                        summary={summary}
                        isDataLoaded={isDataLoaded}
                        setWearerBundleData={setWearerBundleData}
                    />
                ) : (
                    <SubmitButtonDummy />
                )}
            </div>
        </div>
    );
};
